(ns hai.routes
  "Top-level route aggregator for the web-app SPA.

   Owns the flat list of /app/<foo> routes plus the legacy /admin subtree.
   `all-routes` returns a sequence of route entries that web_app.cljs splices
   under /app."
  (:require
   [hai.component-library :as component-library]
   [hai.account.credits.routes :as credits]
   [hai.account.refer.routes :as refer]
   [hai.account.routes :as account]
   [hai.admin-ui.activities.routes :as activities]
   [hai.admin-ui.data-tasks.routes :as data-tasks-routes]
   [hai.admin-ui.profile :as profile]
   [hai.admin-ui.setup :as setup]
   [hai.admin-ui.usage :as usage]
   [hai.admin-ui.users.routes :as users]
   [hai.home.routes :as home]
   [hai.interfaces.routes :as interfaces]
   [hai.journal2.routes :as journal2]
   [hai.library.connections.routes :as connections]
   [hai.library.documents.routes :as documents]
   [hai.library.prompts.routes :as prompts]
   [hai.library.routes :as library]
   [hai.library.sources.routes :as sources]
   [hai.library.tables.routes :as tables]
   [hai.notes.routes :as notes]
   [hai.tasks.routes :as tasks]
   [hai.tags.routes :as tags]
   [hai.chats.routes :as chats]
   [hai.mini-apps.routes :as mini-apps]
   [hai.knowledge.routes :as knowledge]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.web-app.util.params :as params-util]
   [hai.workspaces.routes :as workspaces]
   [hai.workspaces.teams.routes :as teams]))

;; ── Legacy /admin URLs ──────────────────────────────────────────────────────
;;
;; Routes used to be nested under /admin; they now live at /app/<foo> directly.
;; The wildcard catch-all branches on the matched path:
;;
;; - For /admin/connections/oauth/callback/<provider>: dispatch the oauth
;;   handler in place. Provider configs (Hubspot, Notion, Linear, Google,
;;   GitHub, ...) have that exact URL baked in as the redirect_uri, and we
;;   can't change them without per-provider coordination. Handling oauth here
;;   avoids a full-page-reload redirect mid-flow.
;;
;; - Otherwise: rewrite the path to /app/<rest> via window.location.pathname
;;   (search/hash preserved). Keeps old bookmarks, shared links, and emails
;;   working.
;;
;; Implemented as a single wildcard route (rather than a specific oauth route
;; + wildcard) because reitit rejects static-vs-wildcard path conflicts.

(def ^:private oauth-callback-path-re
  #"^connections/oauth/callback/([^/]+)$")

(defn- handle-oauth-callback! [source-type]
  (let [authorization-token (params-util/query-param :authorization_voucher)
        metadata (params-util/query-param :metadata)]
    (>e [:oauth/handle-callback {:source-type source-type
                                 :authorization-token authorization-token
                                 :metadata metadata}])))

(defn- legacy-admin-routes []
  ["/admin"
   ["" {:name :legacy/admin-root
        :controllers [{:start (fn [] (>e [:routing/navigate [:home/view]]))}]}]
   ["/{*path}"
    {:name :legacy/redirect
     :view (fn [_] [:div])
     :controllers
     [{:parameters {:path [:path]}
       :start (fn [{{:keys [path]} :path}]
                (if-let [[_ source-type] (re-matches oauth-callback-path-re path)]
                  (handle-oauth-callback! source-type)
                  (set! js/window.location.pathname (str "/app/" path))))}]}]])

(defn all-routes
  "Returns a sequence of route entries to splice under /app."
  []
  (into [(legacy-admin-routes)]
        cat
        [(mini-apps/routes)
         (knowledge/routes)
         (chats/routes)

         (library/routes)
         (connections/routes)
         (sources/routes)
         (tables/routes)
         (documents/routes)
         (prompts/routes)

         (interfaces/routes)

         (workspaces/routes)
         (teams/routes)
         (users/routes)
         ;; disabled until credit management is needed
         ;; (super-admin/routes)
         (usage/routes)

         (account/routes)
         (credits/routes)
         (refer/routes)
         (profile/routes)

         (activities/routes)
         (setup/routes)
         (journal2/routes)
         (home/routes)
         (notes/routes)
         (tasks/routes)
         (tags/routes)
         (component-library/routes)

         data-tasks-routes/all]))
